import React, { useState, useEffect, useContext, useCallback } from 'react';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import ActivityHistoryTable from '../../../activityHistory/components/ActivityHistoryTable';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer'; // Assuming Footer is used
import companyInfo from 'context/companyInfo'; // Assuming companyInfo is used

import { UserContext } from 'context/userContext';
import { useNotifications } from 'context/NotificationContext'; // Import useNotifications

import { deleteActivity, getActivity, updateActivitySettings } from 'services/activity';
import ArgonBox from 'components/ArgonBox'; // For layout if needed

function RecentActivity() {
  const { userId } = useContext(UserContext);
  const { addNotification } = useNotifications();

  const [isLoading, setIsLoading] = useState(true);
  // const [error, setError] = useState(null); // Replaced by notifications
  const [logs, setLogs] = useState([]);
  const [currentTimeoutSetting, setCurrentTimeoutSetting] = useState(24); // Default to 24 hours

  const fetchActivities = useCallback(
    async (showLoadingNotification = true) => {
      if (!userId) {
        // console.log('User not available for fetching activities.');
        setLogs([]); // Clear logs if no user
        setIsLoading(false);
        return;
      }

      if (showLoadingNotification) {
        addNotification({ title: ['Loading Activities'], details: 'Fetching activity history...', severity: 'info' });
      }
      setIsLoading(true);

      try {
        // Optional: Call deleteActivity if it's meant to be a cleanup before fetching
        // await deleteActivity(userId);
        // console.log('Activity cleanup completed (if applicable).');

        const response = await getActivity(userId);
        if (response.error) {
          addNotification({
            title: ['Error Fetching Logs'],
            details: response.msg || 'Could not retrieve activity logs.',
            severity: 'error',
          });
          setLogs([]);
        } else {
          setLogs(response.data?.activities || []);
        }
      } catch (err) {
        console.error('Error fetching activity logs:', err);
        addNotification({
          title: ['Fetch Error'],
          details: 'Failed to load activity history. Please try again.',
          severity: 'error',
        });
        setLogs([]);
      } finally {
        setIsLoading(false);
      }
    },
    [userId, addNotification]
  );

  const handleUpdateTimeoutSetting = useCallback(
    async (hours) => {
      if (!userId) {
        addNotification({ title: ['Update Failed'], details: 'User not identified.', severity: 'error' });
        return;
      }
      addNotification({
        title: ['Updating Settings'],
        details: `Setting activity log timeout to ${hours} hours...`,
        severity: 'info',
      });
      try {
        const response = await updateActivitySettings(userId, hours);
        if (response.error) {
          addNotification({
            title: ['Settings Update Failed'],
            details: response.msg || `Could not update timeout to ${hours} hours.`,
            severity: 'error',
          });
        } else {
          addNotification({
            title: ['Settings Updated'],
            details: `Activity log timeout successfully set to ${hours} hours. ${response.msg || ''}`,
            severity: 'success',
          });
          setCurrentTimeoutSetting(hours); // Update local state to reflect change

          // Optional: Trigger activity cleanup after successful setting update
          if (userId) {
            addNotification({
              title: ['Cleanup Initiated'],
              details: 'Running activity log cleanup based on new settings...',
              severity: 'info',
            });
            deleteActivity(userId)
              .then((cleanupResponse) => {
                if (cleanupResponse.error) {
                  addNotification({
                    title: ['Cleanup Error'],
                    details: cleanupResponse.msg || 'Error during post-setting activity cleanup.',
                    severity: 'error',
                  });
                } else {
                  addNotification({
                    title: ['Cleanup Successful'],
                    details: cleanupResponse.msg || 'Activity cleanup completed.',
                    severity: 'success',
                  });
                }
                fetchActivities(false); // Refresh logs after cleanup without loading notification
              })
              .catch((cleanupError) => {
                console.error('Error during post-setting activity cleanup:', cleanupError);
                addNotification({
                  title: ['Cleanup Exception'],
                  details: 'An exception occurred during cleanup.',
                  severity: 'error',
                });
              });
          }
        }
      } catch (err) {
        console.error('Error updating timeout setting:', err);
        addNotification({
          title: ['Settings Update Error'],
          details: 'An unexpected error occurred while updating settings.',
          severity: 'error',
        });
      }
    },
    [userId, addNotification, fetchActivities]
  );

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  return (
    <ArgonBox py={3}>
      <ActivityHistoryTable
        logs={logs}
        isLoading={isLoading}
        onRefreshLogs={() => fetchActivities(true)} // Pass explicit refresh with loading notification
        currentTimeout={currentTimeoutSetting}
        onUpdateTimeout={handleUpdateTimeoutSetting}
      />
    </ArgonBox>
  );
}
export default RecentActivity;
